import React, { useState } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { createERPIntegration, updateERPIntegration } from '../../../services/erpIntegrationService';

const IntegrationConfigModal = ({ integration, erpSystems, companyId, onClose }) => {
  const isEdit = !!integration;
  const [formData, setFormData] = useState({
    erp_system_id: integration?.erp_system_id || '',
    erp_system_name: integration?.erp_system_name || '',
    api_endpoint: integration?.api_endpoint || '',
    api_key: integration?.api_key || '',
    username: integration?.username || '',
    password: integration?.password || '',
    sync_frequency: integration?.sync_frequency || 'daily',
    auto_sync_enabled: integration?.auto_sync_enabled || false,
    status: integration?.status || 'active'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSystemChange = (systemId) => {
    const system = erpSystems?.find(s => s?.id === systemId);
    setFormData({
      ...formData,
      erp_system_id: systemId,
      erp_system_name: system?.name || '',
      api_endpoint: system?.apiEndpoint || ''
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.erp_system_id) {
      setError('Please select an ERP system');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        company_id: companyId
      };

      if (isEdit) {
        await updateERPIntegration(integration?.id, data);
      } else {
        await createERPIntegration(data);
      }

      onClose(true);
    } catch (err) {
      setError(err?.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Edit Integration' : 'Add ERP Integration'}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ERP System *
            </label>
            <Select
              value={formData?.erp_system_id}
              onChange={(e) => handleSystemChange(e?.target?.value)}
              disabled={isEdit}
              required
            >
              <option value="">Select ERP System</option>
              {erpSystems?.map((system) => (
                <option key={system?.id} value={system?.id}>
                  {system?.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Endpoint
            </label>
            <Input
              type="url"
              value={formData?.api_endpoint}
              onChange={(e) => setFormData({ ...formData, api_endpoint: e?.target?.value })}
              placeholder="https://api.example.com/v1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key / Token
              </label>
              <Input
                type="password"
                value={formData?.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e?.target?.value })}
                placeholder="Enter API key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sync Frequency
              </label>
              <Select
                value={formData?.sync_frequency}
                onChange={(e) => setFormData({ ...formData, sync_frequency: e?.target?.value })}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username (Optional)
              </label>
              <Input
                type="text"
                value={formData?.username}
                onChange={(e) => setFormData({ ...formData, username: e?.target?.value })}
                placeholder="Username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password (Optional)
              </label>
              <Input
                type="password"
                value={formData?.password}
                onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="auto_sync"
              checked={formData?.auto_sync_enabled}
              onChange={(e) => setFormData({ ...formData, auto_sync_enabled: e?.target?.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="auto_sync" className="text-sm text-gray-700">
              Enable automatic synchronization
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={formData?.status}
              onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={() => onClose(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⟳</span>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {isEdit ? 'Update' : 'Create'} Integration
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationConfigModal;